import React, { useMemo, useState } from 'react';
import { formatDate, formatDateTime, formatMonth, formatRangeOfDays, parseDate } from '../../../../../../util/util';
import { Stack } from '../../../../../../components/Stack/Stack';
import { get, omit } from 'lodash';
import { Card } from '../../../../../../components/Card/Card';
import { LineAreaChart, LineAreaChartValue } from '../../../../../../components/Charts/LineAreaChart/LineAreaChart';
import { IntervalField } from '../../../../../../components/IntervalField/IntervalField';
import { graphql } from '../../../../../../generated';
import { SalesOverTimeLine, SalesOverTimePeriod } from '../../../../../../generated/graphql';
import { t } from '../../../../../../locales/translate';
import { NoData } from '../../../../../../components/NoData/NoData';
import { withSuspendedCard } from '../../../../../../util/suspense';
import { useSuspenseQuery } from '../../../../../../util/use_suspense_query';
import { beginningOfDay, endOfDay } from '../../../../../../util/datetime';

const PRODUCT_SALES = graphql(`
  query ProductSalesOverTime($id: ID!, $from: ISO8601DateTime, $to: ISO8601DateTime, $period: SalesOverTimePeriod) {
    product(id: $id) {
      id
      salesOverTime(period: $period, during: { from: $from, to: $to }) {
        date
        quantity
        total
      }
    }
  }
`);

const PRODUCT_RESALES = graphql(`
  query ProductResalesOverTime($id: ID!, $from: ISO8601DateTime, $to: ISO8601DateTime, $period: SalesOverTimePeriod) {
    product(id: $id) {
      id
      resalesOverTime(period: $period, during: { from: $from, to: $to }) {
        date
        quantity: sold
        total: totalSold
      }
    }
  }
`);

const CATEGORY_SALE = graphql(`
  query CategorySalesOverTime($id: ID!, $from: ISO8601DateTime, $to: ISO8601DateTime, $period: SalesOverTimePeriod) {
    category(id: $id) {
      id
      salesOverTime(period: $period, during: { from: $from, to: $to }) {
        date
        quantity
        total
      }
    }
  }
`);

const convertToLineAreaValues = (
  data: SalesOverTimeLine[],
  interval: SalesOverTimePeriod
): (LineAreaChartValue & { date: Date })[] => {
  const dateToDisplay = (date: string): string => {
    const dateDate = parseDate(date)!;
    const toFormat = new Date(dateDate.getUTCFullYear(), dateDate.getUTCMonth(), dateDate.getUTCDate() + 7);
    switch (interval) {
      case 'month':
        return formatMonth(date) || '';
      case 'hour':
        return formatDateTime(date) || '';
      case 'week':
        return formatRangeOfDays([date, toFormat]);
      default:
        return formatDate(date) || '';
    }
  };

  return data.map((d) => ({
    name: dateToDisplay(d.date),
    line: d.total,
    area: d.quantity,
    date: new Date(d.date),
  }));
};

const getTotalPeriodTimeRange = (salesData: SalesOverTimeLine[]): { from: Date; to: Date } => {
  let from: Date | null = null;
  let to: Date | null = null;
  if (salesData.length > 0) {
    const latestDate = parseDate(salesData[salesData.length - 1].date);
    const firstDate = parseDate(salesData[0].date);

    if (latestDate && firstDate) {
      from = firstDate;
      to = endOfDay(latestDate);
    }
  }

  // if no total period can be extracted from query, set as today
  const today = new Date();
  return { from: from ?? beginningOfDay(today), to: to ?? endOfDay(today) };
};

type SaleType = 'product' | 'category';
type QueryType = typeof PRODUCT_SALES | typeof CATEGORY_SALE | typeof PRODUCT_RESALES;

const queries = (resale?: boolean): Record<SaleType, QueryType> => ({
  product: resale ? PRODUCT_RESALES : PRODUCT_SALES,
  category: CATEGORY_SALE,
});

const tooltipLabels: Record<SalesOverTimePeriod, string> = {
  day: t.component.salesOverTime.day,
  week: t.component.salesOverTime.week,
  month: t.component.salesOverTime.month,
  hour: t.component.salesOverTime.hour,
};

interface Props {
  title: string;
  saleId: string;
}

type SalesProps =
  | {
      saleType: 'product';
      resale?: boolean;
    }
  | {
      saleType: 'category';
    };

function SalesOverTime$({ title, saleId, saleType, ...props }: Props & SalesProps): JSX.Element | null {
  const resale: boolean = 'resale' in props ? !!props.resale : false;
  const [intervalValue, setIntervalValue] = useState<{ from?: Date; to?: Date; period: SalesOverTimePeriod }>({
    period: 'day',
  });

  const { data: sales } = useSuspenseQuery(queries(resale)[saleType], {
    variables: {
      id: saleId,
      period: intervalValue.period,
      from: intervalValue.from,
      to: intervalValue.to,
    },
  });

  const dataKey = resale ? 'resalesOverTime' : 'salesOverTime';
  const typedSales: SalesOverTimeLine[] = get(sales, `${saleType}.${dataKey}`, []);
  const convertedSales = convertToLineAreaValues(typedSales, intervalValue.period);

  const initialDate = useMemo(() => {
    return getTotalPeriodTimeRange(typedSales);
  }, []);

  return (
    <Card title={title}>
      <Stack size="lg">
        <>
          <IntervalField
            initialValue={initialDate}
            value={{
              from: intervalValue.from || initialDate.from,
              to: intervalValue.to || initialDate.to,
              period: intervalValue.period,
            }}
            onChange={(v) => {
              if (v?.from && v?.to && v?.period) {
                setIntervalValue({
                  from: v.from,
                  to: v.to,
                  period: v.period,
                });
              }
            }}
          />

          {convertedSales.length > 0 ? (
            <LineAreaChart
              dataset={convertedSales.map((sale) => omit(sale, 'date'))}
              lineLabel={t.component.salesOverTime.totalSales}
              areaLabel={tooltipLabels[intervalValue.period]}
            />
          ) : (
            <NoData description={t.model.sale.noSalesInPeriod} />
          )}
        </>
      </Stack>
    </Card>
  );
}

export const SalesOverTime = withSuspendedCard(SalesOverTime$, {
  loadingTitle: t.component.salesOverTime.title,
  loadingHeight: 632,
});
